import React, { useState } from "react";
import emailjs from '@emailjs/browser';
import Hero from "../components/Hero";

/* ── ABOUT ────────────────────────────────────── */
function About() {
  return (
    <section id="about" style={{ padding: '8rem 10%' }}>
      <div className="section-header gs_reveal" style={{ marginBottom: '4rem' }}>
        <span className="section-tag">Who we are</span>
        <h2 className="section-title">Engineers &amp; Creatives.</h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
        {/* Text */}
        <div className="gs_reveal">
          <p style={{ fontSize: '1.1rem', color: '#ccc', lineHeight: 1.8, marginBottom: '1.5rem' }}>
            At CloudNest, we bridge the gap between stunning design and hardcore engineering.
            We don't just build websites; we engineer high-performance platforms designed to scale globally.
          </p>
          <p style={{ fontSize: '1.1rem', color: '#ccc', lineHeight: 1.8 }}>
            Our team consists of veteran developers, cloud architects, and UI/UX specialists dedicated
            to pushing the boundaries of what is possible on the web.
          </p>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
          {[
            { number: '150+', label: 'Projects Delivered' },
            { number: '99%', label: 'Client Satisfaction' },
            { number: '24/7', label: 'Support' },
            { number: '10+', label: 'Awards Won' },
          ].map(({ number, label }) => (
            <div key={label} className="stat-box gs_reveal">
              <div className="stat-number">{number}</div>
              <div style={{ color: '#aaa' }}>{label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── SERVICES ──────────────────────────────────── */
function Services() {
  const services = [
    { icon: 'fa-solid fa-code',          title: 'Web Architecture',    desc: 'Enterprise-grade web applications built with React, Vue, and Next.js, backed by robust Node and Python architectures.' },
    { icon: 'fa-solid fa-cloud',         title: 'Cloud Solutions',     desc: 'AWS and Azure integrations, CI/CD pipelines, containerization with Docker & Kubernetes for zero-downtime deployments.' },
    { icon: 'fa-solid fa-cube',          title: 'Immersive UI/UX',     desc: 'Award-winning frontend experiences utilizing WebGL, Three.js, and GSAP for fluid, unforgettable user journeys.' },
    { icon: 'fa-solid fa-mobile-screen', title: 'Mobile Development',  desc: 'Cross-platform mobile applications using React Native and Flutter, delivering native performance on iOS and Android.' },
  ];

  return (
    <section
      id="services"
      style={{
        padding: '8rem 10%',
        background: 'linear-gradient(to bottom, transparent, #111111, transparent)',
      }}
    >
      <div className="section-header gs_reveal" style={{ textAlign: 'center', marginBottom: '4rem' }}>
        <span className="section-tag">What we do</span>
        <h2 className="section-title">Our Expertise.</h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
        {services.map(({ icon, title, desc }) => (
          <div key={title} className="service-card gs_reveal">
            <i className={icon} style={{ fontSize: '2.5rem', color: '#00ffcc', marginBottom: '1.5rem', display: 'block' }} />
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>{title}</h3>
            <p style={{ color: '#aaa', lineHeight: 1.6 }}>{desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── TECH STACK (Infinite Marquee) ────────────────── */
function TechStack() {
  const techs = [
    { icon: 'fa-brands fa-react',   label: 'React/Next.js' },
    { icon: 'fa-brands fa-node-js', label: 'Node.js' },
    { icon: 'fa-brands fa-python',  label: 'Python' },
    { icon: 'fa-brands fa-aws',     label: 'AWS Cloud' },
    { icon: 'fa-brands fa-docker',  label: 'Docker' },
    { icon: 'fa-solid fa-database', label: 'PostgreSQL' },
  ];

  return (
    <section
      id="tech"
      style={{
        paddingTop: 0,
        paddingBottom: '8rem',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        position: 'relative',
        zIndex: 2,
        background: '#050505',
      }}
    >
      {/* Duplicate items enough times to prevent visible gaps on ultrawide screens */}
      <div className="tech-track" style={{ display: 'flex', width: 'max-content' }}>
        {[...techs, ...techs, ...techs, ...techs].map(({ icon, label }, i) => (
          <div key={i} className="tech-item" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', color: '#888', fontSize: '1.2rem', padding: '0 4rem' }}>
            <i className={icon} style={{ fontSize: '4rem', color: 'white', transition: 'color 0.3s' }} />
            <span>{label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── WORK / PORTFOLIO (Horizontal Scroll) ──────────── */
function Work() {
  const projects = [
    {
      title: 'FinTech Dashboard',
      tech: 'React, Redux, Node.js, WebSockets',
      img: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&w=1200&q=80',
    },
    {
      title: 'E-Commerce Platform',
      tech: 'Next.js, Stripe, PostgreSQL',
      img: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80',
    },
    {
      title: 'Cybersecurity Portal',
      tech: 'Vue.js, Python, AWS',
      img: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=1200&q=80',
    },
    {
      title: '3D Configurator',
      tech: 'Three.js, GSAP, WebGL',
      img: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=1200&q=80',
    },
  ];

  return (
    <section
      id="work"
      className="work-section-container"
      style={{ overflow: 'hidden', paddingLeft: '10%', paddingRight: 0, paddingTop: '8rem', paddingBottom: '8rem', position: 'relative', zIndex: 2, background: '#050505' }}
    >
      <div className="section-header" style={{ marginBottom: '4rem' }}>
        <span className="section-tag">Portfolio</span>
        <h2 className="section-title">Featured Projects.</h2>
      </div>

      <div className="work-grid" style={{ display: 'flex', gap: '3rem', width: 'max-content', paddingRight: '10%' }}>
        {projects.map(({ title, tech, img }) => (
          <div
            key={title}
            className="project-card"
            style={{ position: 'relative', borderRadius: '15px', overflow: 'hidden', width: '60vw', maxWidth: '800px', height: '60vh', flexShrink: 0 }}
          >
            <div
              className="project-bg"
              style={{ backgroundImage: `url('${img}')` }}
            />
            <div className="project-overlay">
              <h3>{title}</h3>
              <p style={{ fontSize: '1.1rem', color: '#ddd' }}>{tech}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── CONTACT ───────────────────────────────────── */
function ContactSection() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus('');

    emailjs
      .send('service_qckswib', 'template_30sb7zi', {
        from_name: formData.name,
        from_email: formData.email,
        subject: formData.subject,
        message: formData.message,
      }, 'MW_XPWgdZesbVPMSY')
      .then(() => {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
        setLoading(false);
      })
      .catch((err) => {
        console.error('EmailJS Error:', err);
        setStatus('error');
        setLoading(false);
      });
  };

  return (
    <section
      id="contact"
      style={{ padding: '8rem 10%', paddingBottom: '10rem', background: '#111111', position: 'relative', zIndex: 2 }}
    >
      <div className="contact-container" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem' }}>
        {/* Info */}
        <div className="gs_reveal">
          <span className="section-tag">Get in touch</span>
          <h2 className="section-title" style={{ marginBottom: '2rem' }}>
            Let's build<br />something.
          </h2>
          <div className="contact-info" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <p style={{ fontSize: '1.2rem', color: '#aaa', display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <i className="fa-solid fa-envelope" style={{ color: '#00ffcc' }} />
              cloudnest.dev.solutions@gmail.com
            </p>
            <p style={{ fontSize: '1.2rem', color: '#aaa', display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <i className="fa-solid fa-phone" style={{ color: '#00ffcc' }} />
              +94764231328
            </p>
            <p style={{ fontSize: '1.2rem', color: '#aaa', display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <i className="fa-solid fa-location-dot" style={{ color: '#00ffcc' }} />
              Sri Lanka & Jordan
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="gs_reveal">
          <form className="contact-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <input type="text" name="name" placeholder="Your Name" value={formData.name} onChange={handleChange} required />
            <input type="email" name="email" placeholder="Your Email" value={formData.email} onChange={handleChange} required />
            <input type="text" name="subject" placeholder="Subject" value={formData.subject} onChange={handleChange} />
            <textarea name="message" placeholder="Tell us about your project..." value={formData.message} onChange={handleChange} required />
            <button type="submit" className="cta-btn-solid" style={{ width: '100%' }} disabled={loading}>
              {loading ? 'Sending...' : 'Send Message'}
            </button>
            {status === 'success' && (
              <p style={{ color: '#00ffcc', textAlign: 'center' }}>Message sent! We will get back to you soon.</p>
            )}
            {status === 'error' && (
              <p style={{ color: '#ff4444', textAlign: 'center' }}>Failed to send. Please email us directly.</p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

/* ── HOME PAGE ─────────────────────────────────── */
function Home() {
  return (
    <>
      <Hero />
      {/* main-content wraps all sections — sits on top of Three.js bg, reveals footer on scroll end */}
      <div style={{ position: 'relative', zIndex: 2, background: '#050505', boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}>
        <About />
        <Services />
        <TechStack />
        <Work />
        <ContactSection />
      </div>
    </>
  );
}

export default Home;
